import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ageSectionService } from '../../services/api';

// État initial
const initialState = {
  ageSections: [],
  currentAgeSection: null,
  loading: false,
  error: null,
  total: 0
};

// Actions asynchrones
export const fetchAgeSections = createAsyncThunk(
  'ageSections/fetchAgeSections',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ageSectionService.getAll();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAgeSectionById = createAsyncThunk(
  'ageSections/fetchAgeSectionById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await ageSectionService.getById(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createAgeSection = createAsyncThunk(
  'ageSections/createAgeSection',
  async (ageSectionData, { rejectWithValue }) => {
    try {
      const response = await ageSectionService.create(ageSectionData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateAgeSection = createAsyncThunk(
  'ageSections/updateAgeSection',
  async ({ id, ageSectionData }, { rejectWithValue }) => {
    try {
      const response = await ageSectionService.update(id, ageSectionData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteAgeSection = createAsyncThunk(
  'ageSections/deleteAgeSection',
  async (id, { rejectWithValue }) => {
    try {
      await ageSectionService.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Slice
const ageSectionSlice = createSlice({
  name: 'ageSection',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentAgeSection: (state, action) => {
      state.currentAgeSection = action.payload;
    },
    clearCurrentAgeSection: (state) => {
      state.currentAgeSection = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all age sections
      .addCase(fetchAgeSections.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAgeSections.fulfilled, (state, action) => {
        state.loading = false;
        state.ageSections = action.payload.data || action.payload;
        state.total = action.payload.total || state.ageSections.length;
        state.error = null;
      })
      .addCase(fetchAgeSections.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch age section by ID
      .addCase(fetchAgeSectionById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAgeSectionById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentAgeSection = action.payload;
        state.error = null;
      })
      .addCase(fetchAgeSectionById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create age section
      .addCase(createAgeSection.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAgeSection.fulfilled, (state, action) => {
        state.loading = false;
        state.ageSections.push(action.payload);
        state.error = null;
      })
      .addCase(createAgeSection.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update age section
      .addCase(updateAgeSection.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAgeSection.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.ageSections.findIndex(ageSection => ageSection.id === action.payload.id);
        if (index !== -1) {
          state.ageSections[index] = action.payload;
        }
        if (state.currentAgeSection && state.currentAgeSection.id === action.payload.id) {
          state.currentAgeSection = action.payload;
        }
        state.error = null;
      })
      .addCase(updateAgeSection.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete age section
      .addCase(deleteAgeSection.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAgeSection.fulfilled, (state, action) => {
        state.loading = false;
        state.ageSections = state.ageSections.filter(ageSection => ageSection.id !== action.payload);
        if (state.currentAgeSection && state.currentAgeSection.id === action.payload) {
          state.currentAgeSection = null;
        }
        state.error = null;
      })
      .addCase(deleteAgeSection.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, setCurrentAgeSection, clearCurrentAgeSection } = ageSectionSlice.actions;

export default ageSectionSlice.reducer;
